// shared-lib/estimate-pricing/designCenter/wallTreatment.js
//
// What a bathroom's walls were given AS A WHOLE, in one place for every
// writer and reader:
//
//   - the treatment TEMPLATES the redesign builds a wall from (full tile, a
//     44" tile band with paint above, full paint) and the builder itself —
//     moved here from `functions/.../designAutomation/wallTreatments.js`
//     (which re-exports them) so the Fixtures & Finishes "Half height" choice
//     lays exactly the band the redesign lays;
//   - the RECORD saved on the room (`room.wallTreatment`) by the layout apply,
//     Auto Style, the chat finishes tool and a Fixtures & Finishes wall pick;
//   - the WORDS both the server's message ("shower walls tiled to 7′, other
//     walls painted") and the panel's "Walls:" line use for it.
//
// Pure, no environment: the web panel, the cloud tools and the server's
// what-if pricing (a design package's wainscot slot) all require it.

const {
  SHOWER_TILE_HEIGHTS,
  SHOWER_TILE_SNAP_IN,
} = require('../../geometry/showerWallTiling');
const {
  isShowerSurfaceRegion,
  isWainscotRegion,
  isFullCoverageRegion,
  hasOwnRole,
  baseRegionOf,
  WAINSCOT_SURFACE_TYPE,
} = require('./surfaceRegions');

const { wallFaceHeight } = require('../../geometry/wallFace');

const INCH_M = 0.0254;

/** The wainscot's tile runs this far up every wall, whatever its height. */
const WAINSCOT_HEIGHT_IN = 44;

/**
 * The per-wall templates. `half-tile`'s band is a WAINSCOT, marked as one: a
 * whole-room Wall Paint pick keeps it and a Wall Tile pick takes it
 * (`replaceBaseFinish`, #2712). Without the marker it is indistinguishable
 * from a tile patch drawn by hand, which a whole-room pick removes.
 */
const WALL_TREATMENTS = {
  'full-tile': {
    name: 'Full Tile',
    tileHeightFraction: 1.0,
    regions: [{ role: 'tile', x: 0, y: 0, width: 100, height: 100 }],
  },
  'half-tile': {
    name: 'Half-Height Tile + Paint',
    tileHeightFraction: 0.4,
    regions: [
      {
        role: 'tile',
        surfaceType: WAINSCOT_SURFACE_TYPE,
        x: 0,
        y: 0,
        width: 100,
        height: 40,
      },
      { role: 'paint', x: 0, y: 40, width: 100, height: 60 },
    ],
  },
  'full-paint': {
    name: 'Full Paint',
    tileHeightFraction: 0,
    regions: [{ role: 'paint', x: 0, y: 0, width: 100, height: 100 }],
  },
};

/**
 * The tile height for one wall as a percentage of THAT wall, so the tile line
 * sits at the same absolute height (`WAINSCOT_HEIGHT_IN`) on every wall of
 * the room regardless of how tall each one is.
 *
 * @param {object} template - a `WALL_TREATMENTS` entry
 * @param {number} maxWallHeight - the room's tallest wall (m)
 * @param {number} wallHeight - this wall (m)
 * @returns {number} 0-100
 */
function getTileHeightPercent(template, maxWallHeight, wallHeight) {
  if (!maxWallHeight || !wallHeight || wallHeight <= 0) return 40;
  if (template.tileHeightFraction >= 1.0) return 100;
  return Math.min(100, ((WAINSCOT_HEIGHT_IN * INCH_M) / wallHeight) * 100);
}

/**
 * A wall's regions from a template with a given tile height.
 * @param {object} template - a `WALL_TREATMENTS` entry
 * @param {string|null} tileTextureId - the tile (null for full paint)
 * @param {string} paintColor - hex for the paint regions
 * @param {number} wallIndex - for unique regionIds (`region_<ts>_wall_<n>_<i>`,
 *   the trace `functions/scripts/backfill-wainscot-marker.js` recognises)
 * @param {number} tileHeightPercent - 0-100
 * @param {string} [groupId] - stamped on the WAINSCOT (the template's
 *   marked tile band) and nothing else: the walls' bands one apply makes
 *   share it, which is how the elevations drag moves them as one
 *   (`withWainscot`). Omitted, no region carries a group.
 */
function buildRegionsFromTreatment(
  template,
  tileTextureId,
  paintColor,
  wallIndex,
  tileHeightPercent,
  groupId
) {
  const timestamp = Date.now();
  return template.regions.map((tmpl, i) => {
    let y = tmpl.y;
    let height = tmpl.height;
    if (tileHeightPercent !== undefined && template.regions.length === 2) {
      if (tmpl.role === 'tile') {
        height = tileHeightPercent;
      } else if (tmpl.role === 'paint') {
        y = tileHeightPercent;
        height = 100 - tileHeightPercent;
      }
    }

    if (tmpl.role === 'tile') {
      return {
        regionId: `region_${timestamp}_wall_${wallIndex}_${i}`,
        x: tmpl.x,
        y,
        width: tmpl.width,
        height,
        enabled: true,
        type: 'texture',
        id: tileTextureId,
        ...(tmpl.surfaceType ? { surfaceType: tmpl.surfaceType } : {}),
        ...(groupId && tmpl.surfaceType === WAINSCOT_SURFACE_TYPE
          ? { groupId }
          : {}),
      };
    }
    return {
      regionId: `region_${timestamp}_wall_${wallIndex}_${i}`,
      x: tmpl.x,
      y,
      width: tmpl.width,
      height,
      enabled: true,
      type: 'paint',
      id: `paint_${paintColor.replace('#', '')}`,
      color: paintColor,
    };
  });
}

// ---------------------------------------------------------------------------
// The record
// ---------------------------------------------------------------------------

/**
 * What the walls OTHER than the shower's were given. The shower's own tile is
 * not part of the kind — it is read off the walls (`roomHasShowerTile`), so a
 * later "Match wall" cannot leave the record claiming tile that is gone.
 */
const WALL_TREATMENT_KINDS = Object.freeze({
  PAINT: 'paint', // painted (the layout apply's)
  TILE: 'tile', // every wall tiled
  HALF_TILE: 'half-tile', // a 44" wainscot, painted above
  FIXTURE_HALF: 'fixture-half', // half-height behind the tub and vanity
});
const KNOWN_KINDS = new Set(Object.values(WALL_TREATMENT_KINDS));

/** Who gave the walls their treatment. */
const WALL_TREATMENT_SOURCES = Object.freeze({
  LAYOUT: 'layout', // Choose New Layout / the chat layout tools
  AUTO_STYLE: 'auto-style', // apply_style asked for a style
  FINISHES: 'finishes', // the chat apply_finishes tool
  DESIGN_CENTER: 'design-center', // a Fixtures & Finishes wall pick
  FINISH_EDITOR: 'finish-editor', // All Walls' "Use on every wall in this room"
});

/**
 * `room.wallTreatment`. `appliedAt` is an ISO string (the `createdAt`
 * naming; an ISO string because a room-store write cannot carry a server
 * timestamp). Throws on an unknown kind — a record nobody can describe is a
 * bug at the writer, not something to store.
 */
function wallTreatmentRecord({ kind, source, appliedAt, accentWalls = 0 }) {
  if (!KNOWN_KINDS.has(kind)) {
    throw new Error(`Unknown wall treatment kind: ${kind}`);
  }
  return {
    kind,
    source,
    appliedAt,
    ...(accentWalls > 0 ? { accentWalls } : {}),
  };
}

// ---------------------------------------------------------------------------
// The words
// ---------------------------------------------------------------------------

const HEIGHT_LABELS = {
  [SHOWER_TILE_HEIGHTS.STANDARD]: '7′',
  [SHOWER_TILE_HEIGHTS.FULL]: 'the ceiling',
};

/**
 * The treatment as phrases, most specific first. `showerTiled` says whether
 * the room's shower walls carry tile; `showerTileHeight` adds how high
 * (the message names it, the panel line leaves it to the Shower Wall card).
 */
function describeWallTreatment(
  treatment,
  { showerTiled = false, showerTileHeight = null } = {}
) {
  if (!treatment?.kind) return [];
  const heightLabel = HEIGHT_LABELS[showerTileHeight];
  const shower = showerTiled
    ? heightLabel
      ? `shower walls tiled to ${heightLabel}`
      : 'shower walls tiled'
    : null;
  let parts;
  switch (treatment.kind) {
    case WALL_TREATMENT_KINDS.PAINT:
      parts = [shower, showerTiled ? 'other walls painted' : 'walls painted'];
      break;
    case WALL_TREATMENT_KINDS.TILE:
      parts = ['all walls tiled'];
      break;
    case WALL_TREATMENT_KINDS.HALF_TILE:
      parts = [
        shower,
        showerTiled
          ? 'half-height tile on the other walls, painted above'
          : 'half-height tile, painted above',
      ];
      break;
    case WALL_TREATMENT_KINDS.FIXTURE_HALF:
      parts = [
        shower,
        'half-height tile behind the tub and vanity',
        'other walls painted',
      ];
      break;
    default:
      return [];
  }
  const accents = treatment.accentWalls || 0;
  if (accents > 0) {
    parts.push(accents === 1 ? 'one accent wall' : `${accents} accent walls`);
  }
  return parts.filter(Boolean);
}

/** For a message: "shower walls tiled to 7′, other walls painted". */
const wallTreatmentSummary = (treatment, opts) =>
  describeWallTreatment(treatment, opts).join(', ');

/** For the panel: "Walls: shower walls tiled · other walls painted", or null. */
function wallTreatmentLine(treatment, { showerTiled = false } = {}) {
  const parts = describeWallTreatment(treatment, { showerTiled });
  return parts.length ? `Walls: ${parts.join(' · ')}` : null;
}

// ---------------------------------------------------------------------------
// Reading the room
// ---------------------------------------------------------------------------

const regionsOf = (wall) => wall?.texture?.regions || [];

/** The wall carries shower tile (a stall's or a tub/shower combo's). */
const isShowerTiledWall = (wall) => regionsOf(wall).some(isShowerSurfaceRegion);

const roomHasShowerTile = (walls) => (walls || []).some(isShowerTiledWall);

// Paint with nothing saying what it is for: a whole-wall base, or the band
// above half-height tile.
const isPlainPaint = (r) => r?.type === 'paint' && !hasOwnRole(r);

const paintHexOf = (region) =>
  region.color ||
  (typeof region.id === 'string' && region.id.startsWith('paint_')
    ? `#${region.id.slice('paint_'.length)}`
    : null);

/**
 * The room's wall paint, `{ id, color }` — a wall's painted base, else the
 * band above half-height tile — or null when no wall is painted (an all-tile
 * room). What the wainscot puts above its tile.
 */
function currentWallPaint(walls) {
  for (const wall of walls || []) {
    const base = baseRegionOf(wall);
    if (base?.type === 'paint' && paintHexOf(base)) {
      return { id: base.id, color: paintHexOf(base) };
    }
  }
  for (const wall of walls || []) {
    const band = regionsOf(wall).find(isPlainPaint);
    if (band && paintHexOf(band)) {
      return { id: band.id, color: paintHexOf(band) };
    }
  }
  return null;
}

/** The wainscot's tile, from the first wall carrying one, or null. */
function wainscotTextureOf(walls) {
  for (const wall of walls || []) {
    const band = regionsOf(wall).find(isWainscotRegion);
    if (band?.id) return band.id;
  }
  return null;
}

// ---------------------------------------------------------------------------
// The half-height (wainscot) choice
// ---------------------------------------------------------------------------

const maxHeightOf = (walls) =>
  (walls || []).reduce((max, w) => Math.max(max, w?.dimensions?.y || 0), 0);

const clampPct = (v) => Math.min(100, Math.max(0, v));

/**
 * Where a wall's shower tile runs along it, as merged `[start, end]` spans in
 * the wall's region percent — read off the wall's own shower regions, the
 * ones the 3D draws and the take-off bills (a missing `x` / `width` read as
 * the take-off's `regionRectOnWall` reads it). A span stopping within
 * `SHOWER_TILE_SNAP_IN` of the wall's end runs on to it — the tiler's own
 * rule, which tile cut before it did not follow — so no sliver of wainscot
 * is laid in the corner.
 */
function showerTileSpans(wall) {
  const spans = regionsOf(wall)
    .filter(isShowerSurfaceRegion)
    .map((r) => {
      const x = r.x ?? 0;
      return [clampPct(x), clampPct(x + (r.width ?? 100))];
    })
    .filter(([a, b]) => b > a)
    .sort((p, q) => p[0] - q[0]);
  const merged = [];
  for (const [a, b] of spans) {
    const last = merged[merged.length - 1];
    if (last && a <= last[1]) last[1] = Math.max(last[1], b);
    else merged.push([a, b]);
  }
  const lengthM = wall?.dimensions?.x;
  if (merged.length && lengthM > 0) {
    const snapPct = ((SHOWER_TILE_SNAP_IN * INCH_M) / lengthM) * 100;
    if (merged[0][0] < snapPct) merged[0][0] = 0;
    const last = merged[merged.length - 1];
    if (100 - last[1] < snapPct) last[1] = 100;
  }
  return merged;
}

/** The parts of the wall's width, `[0, 100]`, that no span covers. */
function uncoveredSpans(spans) {
  const out = [];
  let at = 0;
  for (const [a, b] of spans) {
    if (a > at) out.push([at, a]);
    at = Math.max(at, b);
  }
  if (at < 100) out.push([at, 100]);
  return out;
}

/** A full-width region laid over one span instead, its id told apart. */
const inSpan = (region, [a, b], tag) => ({
  ...region,
  regionId: `${region.regionId}_${tag}`,
  x: a,
  width: b - a,
});

/**
 * One wall's regions under the "Half height" choice: the redesign's own
 * half-tile template — a 44" band of `textureId`, marked a wainscot, with
 * `paint` above it. On a wall with shower tile the band covers only the part
 * of the wall the tile does NOT (Yu-Han, 2026-10-22) — a piece for each
 * stretch beside the tile, each with its paint above, and plain `paint` the
 * wall's full height under the tile's span, so the tile's height can change
 * (Standard / Full) with no gap and no overlap; a wall the tile covers end to
 * end is painted, as before. The shower tile stays on top, and everything
 * else with a role of its own (a backsplash, a tub surround) is kept over
 * it; the old band, the room's old paint and a tile patch drawn by hand are
 * replaced. `groupId` is the apply's one wainscot group.
 *
 * @param {object} wall
 * @param {number} wallIndex
 * @param {{ textureId: string, paint: { id: string, color: string },
 *           maxWallHeight?: number, groupId?: string }} choice
 */
function wainscotRegionsFor(
  wall,
  wallIndex,
  { textureId, paint, maxWallHeight, groupId }
) {
  const kept = regionsOf(wall).filter(
    (r) => hasOwnRole(r) && !isWainscotRegion(r)
  );
  // The DRAWN face's height (`wallFace`): the tile line's percentage is of
  // the face the 3D tiles over, so it lands at 44" on a face taller or
  // shorter than the wall's stored box too.
  const wallHeight = wallFaceHeight(wall) || maxWallHeight;
  return layWainscot(wall, wallIndex, {
    textureId,
    paint,
    tilePct: getTileHeightPercent(
      WALL_TREATMENTS['half-tile'],
      maxWallHeight || wallHeight,
      wallHeight
    ),
    groupId,
    kept,
  });
}

/**
 * The wainscot laid on one wall, beside its shower tile — THE split, which
 * the Fixtures & Finishes apply (`wainscotRegionsFor`) and the re-split after
 * a shower changes (`resplitWainscot`) both run: a band of `textureId`
 * `tilePct` up, `paint` above it; on a wall with shower tile, a piece for
 * each stretch beside the tile, each with its paint above, and `paint` the
 * wall's full height under the tile's span; a wall the tile covers end to end
 * painted. `kept` (the regions with a role of their own) go on top.
 */
function layWainscot(
  wall,
  wallIndex,
  { textureId, paint, tilePct, groupId, kept }
) {
  const build = (key) =>
    buildRegionsFromTreatment(
      WALL_TREATMENTS[key],
      textureId,
      paint.color,
      wallIndex,
      tilePct,
      groupId
    ).map((r) => (r.type === 'paint' ? { ...r, id: paint.id } : r));
  const tiled = showerTileSpans(wall);
  const free = uncoveredSpans(tiled);
  if (!tiled.length) return [...build('half-tile'), ...kept];
  if (!free.length) return [...build('full-paint'), ...kept];
  const underTile = tiled.flatMap((span, k) =>
    build('full-paint').map((r) => inSpan(r, span, `u${k}`))
  );
  const beside = free.flatMap((span, k) =>
    build('half-tile').map((r) => inSpan(r, span, `s${k}`))
  );
  return [...underTile, ...beside, ...kept];
}

const SAME_PCT = 1e-6;
const sameSpans = (a, b) =>
  a.length === b.length &&
  a.every(
    (s, i) =>
      Math.abs(s[0] - b[i][0]) < SAME_PCT && Math.abs(s[1] - b[i][1]) < SAME_PCT
  );
/** A wall's wainscot pieces' spans, merged, left to right. */
const pieceSpans = (wall) =>
  regionsOf(wall)
    .filter(isWainscotRegion)
    .map((r) => [r.x ?? 0, (r.x ?? 0) + (r.width ?? 100)])
    .sort((p, q) => p[0] - q[0])
    .reduce((out, [a, b]) => {
      const last = out[out.length - 1];
      if (last && a <= last[1] + SAME_PCT) last[1] = Math.max(last[1], b);
      else out.push([a, b]);
      return out;
    }, []);

/**
 * The paint a wall's wainscot is laid in: the band sitting on a piece's top,
 * else the wall's full-coverage paint (a later Paint pick lays one), else any
 * plain paint on it. Null when it has none.
 */
function layoutPaintOf(regions, topPct) {
  const plain = regions.filter((r) => isPlainPaint(r) && paintHexOf(r));
  const found =
    (topPct != null &&
      plain.find((r) => Math.abs((r.y ?? 0) - topPct) < SAME_PCT)) ||
    plain.find(isFullCoverageRegion) ||
    plain[0];
  return found ? { id: found.id, color: paintHexOf(found) } : null;
}

/** Where a wall's shower tile runs, as the split would leave it bare. */
const freeSpansOf = (wall) => uncoveredSpans(showerTileSpans(wall));

/**
 * The room's wainscot, to lay on a wall that has none of its own: the tile,
 * group and real height of the first piece found (a grouped one first).
 */
function roomWainscotOf(walls) {
  let found = null;
  for (const w of walls || []) {
    for (const r of regionsOf(w)) {
      if (!isWainscotRegion(r)) continue;
      if (!found || (!found.piece.groupId && r.groupId))
        found = { piece: r, wall: w };
    }
  }
  if (!found) return null;
  const storedH = found.wall?.dimensions?.y;
  const topPct = (found.piece.y ?? 0) + found.piece.height;
  return {
    textureId: found.piece.id,
    groupId: found.piece.groupId,
    heightM: storedH > 0 ? (topPct / 100) * storedH : null,
    topPct,
  };
}

/**
 * Re-lay a wall's wainscot beside its shower tile as it now stands — after a
 * shower moved, resized or went, "Match wall", or a re-cut that shifted old
 * tile (Yu-Han, 2026-10-22: the wainscot always meets the tile). The same
 * split as the apply (`layWainscot`), keeping the wainscot's tile, group and
 * height and the paint it was laid in; the wall's other regions are kept —
 * a base in another colour under the wainscot, everything else (the tile, a
 * backsplash, a patch of tile or paint drawn by hand) over it, in its order.
 * A wall with no wainscot takes the room's (`template`, from
 * `resplitWainscotWalls`) only when its tile no longer covers it end to end
 * and did before. Given `before`, a wall whose tile has not moved is left
 * alone; a wall that already fits is handed back as it was; a wall with no
 * plain paint to lay the wainscot's paint in is left as it is.
 *
 * @param {object} wall
 * @param {number} wallIndex
 * @param {{template?: object, before?: object}} [ctx]
 * @returns {object} the wall, or a new one with its regions re-laid
 */
function resplitWainscot(
  wall,
  wallIndex,
  { template = null, before = null } = {}
) {
  const regions = regionsOf(wall);
  const pieces = regions.filter(isWainscotRegion);
  // A re-split answers a change of the TILE: a wall whose tile runs where it
  // ran is left alone, whatever it holds.
  if (before && sameSpans(showerTileSpans(before), showerTileSpans(wall))) {
    return wall;
  }
  const free = freeSpansOf(wall);
  let spec;
  if (pieces.length) {
    if (sameSpans(pieceSpans(wall), free)) return wall;
    const first = pieces[0];
    spec = {
      textureId: first.id,
      groupId: first.groupId,
      tilePct: (first.y ?? 0) + first.height,
    };
  } else {
    // Only a wall its tile covered end to end, that now shows some of it.
    const wasCovered = before && !freeSpansOf(before).length;
    if (!template || !wasCovered || !free.length) return wall;
    const storedH = wall?.dimensions?.y;
    spec = {
      textureId: template.textureId,
      groupId: template.groupId,
      tilePct:
        template.heightM != null && storedH > 0
          ? Math.min(100, (template.heightM / storedH) * 100)
          : template.topPct,
    };
  }
  const paint = layoutPaintOf(regions, pieces.length ? spec.tilePct : null);
  if (!paint) return wall;
  // Only what the split itself lays is re-laid: the wainscot, the band of
  // its paint on a piece's top, the strips of it under the tile — or the
  // full-coverage base a later Paint pick left. Paint drawn by hand is the
  // user's: it stays, over the wainscot, in its order.
  const layoutPaint = (r) =>
    isPlainPaint(r) &&
    r.id === paint.id &&
    ((Math.abs((r.y ?? 0) - spec.tilePct) < SAME_PCT &&
      Math.abs((r.y ?? 0) + (r.height ?? 100) - 100) < SAME_PCT) ||
      (Math.abs(r.y ?? 0) < SAME_PCT &&
        Math.abs((r.height ?? 100) - 100) < SAME_PCT));
  const others = regions.filter((r) => !isWainscotRegion(r) && !layoutPaint(r));
  // a base in another colour stays the base, under everything
  const base = (r) => isPlainPaint(r) && isFullCoverageRegion(r);
  const laid = layWainscot(wall, wallIndex, {
    ...spec,
    paint,
    kept: others.filter((r) => !base(r)),
  });
  return {
    ...wall,
    texture: {
      ...(wall.texture || {}),
      regions: [...others.filter(base), ...laid],
    },
  };
}

/**
 * The walls as they stand, each with its own copy of its regions — for a
 * writer that edits regions IN PLACE (the cloud tilers, the browser's
 * recalculators), so the re-split can still read where the tile ran before.
 */
const snapshotWallRegions = (walls) =>
  (walls || []).map((w) =>
    w
      ? {
          ...w,
          texture: {
            ...(w.texture || {}),
            // each region copied too: a recalculator writes into them
            // (`region.x = …`)
            regions: regionsOf(w).map((r) => ({ ...r })),
          },
        }
      : w
  );

/**
 * Every wall of a room re-split after its shower tile changed
 * (`resplitWainscot`). `before` is the walls as they were — matched by id,
 * else by place in the list — which says whose tile moved and which walls the
 * tile covered end to end. The list is handed back as it was when no wall
 * changed.
 */
function resplitWainscotWalls(walls, before = null) {
  if (!Array.isArray(walls)) return walls;
  const template = roomWainscotOf(walls) || roomWainscotOf(before);
  const beforeById = new Map(
    (before || []).filter((w) => w?.id).map((w) => [w.id, w])
  );
  let changed = false;
  const out = walls.map((w, i) => {
    if (!w) return w;
    const next = resplitWainscot(w, i, {
      template,
      before: (w.id && beforeById.get(w.id)) || before?.[i] || null,
    });
    if (next !== w) changed = true;
    return next;
  });
  return changed ? out : walls;
}

/**
 * The `groupId` the wainscots of ONE apply share — Fixtures & Finishes'
 * "Half height" (`withWainscot`) and Auto Style (`applyStyle`) alike: mint one
 * per apply and hand it to `buildRegionsFromTreatment` for every wall, which
 * stamps it on the wainscot alone. (`'group_' + time + '_' + 9 random`, the
 * shape the viewer's "Add …" buttons mint.)
 */
const newWainscotGroupId = () =>
  `group_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;

/**
 * Every wall of a room under the "Half height" choice. The wainscots this
 * one apply lays share ONE `groupId` — they were made together, so the
 * elevations drag moves them together (Yu-Han, 2026-10-22). A re-apply
 * replaces them, so it gives an older, ungrouped wainscot a group.
 */
function withWainscot(walls, { textureId, paint }) {
  const maxWallHeight = maxHeightOf(walls);
  const groupId = newWainscotGroupId();
  return (walls || []).map((wall, i) =>
    wall
      ? {
          ...wall,
          texture: {
            ...(wall.texture || {}),
            regions: wainscotRegionsFor(wall, i, {
              textureId,
              paint,
              maxWallHeight,
              groupId,
            }),
          },
        }
      : wall
  );
}

/**
 * The kind a set of walls is, after a Fixtures & Finishes wall pick: a
 * wainscot anywhere is half-height tile (a Paint pick keeps one, #2712), else
 * the first wall's own finish — paint or tile. Null when no wall has one.
 */
function wallKindAfterPick(walls) {
  if ((walls || []).some((w) => regionsOf(w).some(isWainscotRegion))) {
    return WALL_TREATMENT_KINDS.HALF_TILE;
  }
  for (const wall of walls || []) {
    const base = baseRegionOf(wall);
    if (base?.type === 'paint') return WALL_TREATMENT_KINDS.PAINT;
    if (base) return WALL_TREATMENT_KINDS.TILE;
  }
  return null;
}

module.exports = {
  WALL_TREATMENTS,
  WAINSCOT_HEIGHT_IN,
  getTileHeightPercent,
  buildRegionsFromTreatment,
  newWainscotGroupId,
  WALL_TREATMENT_KINDS,
  WALL_TREATMENT_SOURCES,
  wallTreatmentRecord,
  describeWallTreatment,
  wallTreatmentSummary,
  wallTreatmentLine,
  isShowerTiledWall,
  roomHasShowerTile,
  currentWallPaint,
  wainscotTextureOf,
  wainscotRegionsFor,
  withWainscot,
  resplitWainscot,
  resplitWainscotWalls,
  snapshotWallRegions,
  wallKindAfterPick,
};
